import { UserName } from "@/components/UserName";
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { LoadMore } from "@/components/LoadMore";
import { Package, Boxes, Truck, ArrowRight, Undo2, History, RotateCcw, User, Clock } from "lucide-react";
import { formatDateTime } from "@/lib/datetime";

const KIND = { part: Package, container: Boxes, donor: Truck };
const PAGE = 50;

export default function MovesHistoryPage() {
  const [filters, setFilters] = useState({ item: "", location: "", user_q: "", day: "" });
  const [moves, setMoves] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  // SERVER-SIDE paging. It used to fetch the first 100 rows and then page THOSE in the browser,
  // so the footer said "showing 50 of 100" while the real log held thousands. "Load more" now
  // asks the server for the next page and the count beside it is the server's true total.
  const fetchPage = useCallback(async (skip) => {
    setLoading(true);
    try {
      const params = { limit: PAGE, skip };
      Object.entries(filters).forEach(([k, v]) => { if (v && v.trim()) params[k] = v.trim(); });
      const { data } = await api.get("/move-item/history", { params });
      const rows = data.moves || [];
      setMoves((prev) => (skip ? [...prev, ...rows] : rows));
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
      setNote(data.note || "");
    } catch {
      if (!skip) { setMoves([]); setTotal(0); setHasMore(false); }
      setNote("Couldn't load moves.");
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchPage(0); }, [fetchPage]);

  const clearFilters = () => setFilters({ item: "", location: "", user_q: "", day: "" });
  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="moves-history-page">
      <div className="flex items-center gap-2 mb-1">
        <History className="w-5 h-5 text-[#B01E28]" />
        <h1 className="font-display text-2xl font-bold tracking-tight">Moves History</h1>
      </div>
      <p className="text-sm text-slate-500 mb-5">Read-only audit of every yard/lot move. To fix an old mistake, physically re-scan the item to where it actually is — this view never rewrites the past.</p>

      {/* FILTERS */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4 p-4 border border-slate-200 bg-white rounded-xl">
        <div><Label className="text-[10px] uppercase tracking-wider text-slate-400">Item code</Label>
          <Input data-testid="filter-item" value={filters.item} onChange={set("item")} placeholder="e.g. TOTE-004" className="rounded-lg border-slate-200 h-9 font-mono" /></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-slate-400">Location code</Label>
          <Input data-testid="filter-location" value={filters.location} onChange={set("location")} placeholder="e.g. SHELF-A" className="rounded-lg border-slate-200 h-9 font-mono" /></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-slate-400">User</Label>
          <Input data-testid="filter-user" value={filters.user_q} onChange={set("user_q")} placeholder="email" className="rounded-lg border-slate-200 h-9" /></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-slate-400">Day</Label>
          <Input data-testid="filter-day" type="date" value={filters.day} onChange={set("day")} className="rounded-lg border-slate-200 h-9" /></div>
        <div className="flex items-end"><Button data-testid="clear-filters" variant="outline" onClick={clearFilters} className="rounded-lg border-slate-200 h-9 w-full"><RotateCcw className="w-3.5 h-3.5 mr-1.5" />Clear</Button></div>
      </div>

      <div className="text-xs text-slate-400 mb-2" data-testid="moves-total">{loading && !moves.length ? "Loading…" : `${total} move${total === 1 ? "" : "s"}${note ? " · " + note : ""}`}</div>

      {/* LIST */}
      <div className="border border-slate-200 bg-white rounded-xl overflow-hidden">
        <div className="hidden md:grid grid-cols-[150px_1fr_1fr_170px_90px] gap-2 bg-slate-100 px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">
          <span>When</span><span>Item</span><span>Move</span><span>User</span><span>Type</span>
        </div>
        {moves.length === 0 && !loading ? (
          <div className="p-8 text-center font-mono text-xs text-slate-400" data-testid="moves-empty">NO MOVES MATCH</div>
        ) : moves.map((m) => {
          const Icon = KIND[m.kind] || Package;
          return (
            <div key={m.tx_number} data-testid="move-row" className={`grid grid-cols-1 md:grid-cols-[150px_1fr_1fr_170px_90px] gap-2 px-4 py-2 border-t border-slate-100 text-sm items-center ${m.is_undo ? "bg-slate-50" : ""}`}>
              <span className="font-mono text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3 text-slate-300" />{formatDateTime(m.timestamp)}</span>
              <span className="flex items-center gap-1.5 min-w-0"><Icon className="w-4 h-4 text-slate-400 shrink-0" /><span className="font-mono font-semibold truncate">{m.item_label}</span>{m.item_sub ? <span className="text-xs text-slate-400 truncate hidden lg:inline">{m.item_sub}</span> : null}</span>
              <span className="flex items-center gap-1.5 font-mono text-xs text-slate-600 min-w-0"><span className="truncate">{m.from_label}</span><ArrowRight className="w-3 h-3 text-slate-300 shrink-0" /><span className="truncate font-semibold text-slate-800">{m.to_label}</span></span>
              <span className="text-xs text-slate-500 flex items-center gap-1 truncate"><User className="w-3 h-3 text-slate-300 shrink-0" /><UserName email={m.user_email} /></span>
              <span>{m.is_undo
                ? <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase text-amber-600"><Undo2 className="w-3 h-3" />undo</span>
                : <span className="text-[10px] font-mono uppercase text-slate-400">move</span>}</span>
            </div>
          );
        })}
      </div>
      {moves.length > 0 && (
        <LoadMore shownCount={moves.length} total={total} hasMore={hasMore}
          onMore={() => fetchPage(moves.length)} noun="move" testid="moves-load-more" />
      )}
    </div>
  );
}
